/**
 * Restaurant cart pickup-time resolution.
 *
 * Purpose: turn a merchant's business hours and lead-time configuration into the single
 * pickup/delivery time the restaurant cart drawer displays — either an ASAP quote or the
 * first schedulable slot.
 *
 * Inputs: a caller-supplied `currentTime` (this module never reads the system clock), the
 * merchant's lead-time and busy-mode configuration, business
 * hours, order type and an optional daypart menu. Delivery estimates and any previously
 * chosen slot are read from session storage.
 * Outputs: an ASAP `{ type, epoch }` object, a localized display string, `""` when a
 * delivery estimate is still missing, or `null` when nothing is schedulable within
 * `maxDays`. Side effect: the resolved choice is written back to session storage.
 *
 * Safe configuration points: `merchant.scheduledOrdering.maxDays` (default 3) bounds the day search.
 * Busy-mode awareness lives entirely in `getEffectiveLeadTime`; `leadTime` and
 * `busyModeFields` travel together and are never reinterpreted here.
 */
import { OrderType, PickupTimeType, daysOfWeek } from "@/constants/enum";
import { DateTime } from "luxon";
import { OrderEstimates } from "@/dto/orderEstimates";
import { ScheduleDateType, ScheduledTime } from "@/dto/scheduledTime";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "./sessionStorageUtils";
import { SessionStorageKey } from "./storageKeys";
import { DayPart } from "@/dto/dayPartMenu";
import {
  SchedulingContext,
  getBusinessHoursOfDay,
  getDeliveryDelayMinutes,
  getFirstAvailableTimeOption,
  getSchedulableDateOptions,
  isWithinBusinessHours,
} from "./scheduleOrderUtils";
import { getEffectiveLeadTime, toBusyModeFields } from "./busyModeLeadTime";
import { Merchant } from "@/dto/merchant";
import { CartItem } from "@/models/cart";
import { GiftCard } from "@/dto/giftCard";
import { TableData } from "@/dto/tableData";

const getFormattedPickupDisplay = (
  pickupDateTime: DateTime,
  today: DateTime,
  tomorrow: DateTime,
  t: any
) => {
  const scheduleDate = {
    displayValue: pickupDateTime.toFormat("MMM d"),
    dateObject: pickupDateTime.toJSDate(),
  };
  const scheduleTime = {
    displayValue: pickupDateTime.toFormat("h:mm a"),
    timeObject: pickupDateTime.toJSDate(),
  };

  if (pickupDateTime.hasSame(today, "day")) {
    return t("todayTime", { formattedTime: scheduleTime.displayValue });
  }

  if (pickupDateTime.hasSame(tomorrow, "day")) {
    return t("tomorrowDateTime", {
      formattedDate: scheduleDate.displayValue,
      formattedTime: scheduleTime.displayValue,
    });
  }

  return `${scheduleDate.displayValue}, ${scheduleTime.displayValue}`;
};

const saveScheduledSession = (
  orderType: OrderType,
  pickupDateTime: DateTime
) => {
  saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
    orderType,
    date: {
      displayValue: pickupDateTime.toFormat("MMM d"),
      dateObject: pickupDateTime.toJSDate(),
    },
    time: {
      displayValue: pickupDateTime.toFormat("h:mm a"),
      timeObject: pickupDateTime.toJSDate(),
    },
  });
};

const getStoredSessionPickupDisplay = (
  sessionTime: ScheduledTime,
  orderType: OrderType,
  timezone: string,
  now: DateTime,
  today: DateTime,
  tomorrow: DateTime,
  t: any
) => {
  if (
    sessionTime.orderType !== orderType ||
    !sessionTime?.date?.dateObject ||
    !sessionTime?.time?.timeObject
  ) {
    return null;
  }

  const storedDateTime = DateTime.fromJSDate(new Date(sessionTime.time.timeObject), {
    zone: timezone,
  });

  if (storedDateTime <= now) {
    return null;
  }

  const isToday = storedDateTime.hasSame(today, "day");
  const isTomorrow = storedDateTime.hasSame(tomorrow, "day");
  let dateDisplay = sessionTime.date.displayValue;

  if (!isTomorrow && dateDisplay.startsWith(t("tomorrow"))) {
    // Capture only the first parenthetical segment with a bounded character class.
    // This avoids greedy/backtracking-heavy patterns that can be ReDoS-prone.
    const parentheticalDateMatch = /\(([^()]*)\)/.exec(dateDisplay);
    const extractedDate = parentheticalDateMatch?.[1];
    if (extractedDate) {
      dateDisplay = extractedDate.trim();
    }
  }

  if (isToday) {
    return t("todayTime", { formattedTime: sessionTime.time.displayValue });
  }

  if (isTomorrow) {
    if (dateDisplay.startsWith(t("tomorrow"))) {
      return `${dateDisplay}, ${sessionTime.time.displayValue}`;
    }

    return t("tomorrowDateTime", {
      formattedDate: dateDisplay,
      formattedTime: sessionTime.time.displayValue,
    });
  }

  return `${dateDisplay}, ${sessionTime.time.displayValue}`;
};

/**
 * Iterate candidate days in order; return the first day that yields a slot.
 * The only thing the two finders disagree on is where the candidate days come from, so that
 * is the sole parameter. Days with no business hours, or with no eligible slot, are skipped.
 */
const findFirstSlotAcrossDays = (
  context: SchedulingContext,
  candidateDates: DateTime[]
): DateTime | null => {
  const { timezone, hours, daypartMenu } = context;

  for (const candidateDate of candidateDates) {
    const day = daysOfWeek[candidateDate.weekday % 7];
    const hoursForDay = getBusinessHoursOfDay(hours, day, daypartMenu);

    if (!hoursForDay?.length) {
      continue;
    }

    const slot = getFirstAvailableTimeOption({
      dateObject: candidateDate.toJSDate(),
      timeRange: hoursForDay,
      context,
    });

    if (!slot) {
      continue;
    }

    // getFirstAvailableTimeOption already applied the busy-mode-aware eligibility check,
    // so the slot it returns needs no further lead-time gating here.
    return DateTime.fromJSDate(slot.timeObject, { zone: timezone });
  }

  return null;
};

const findNextAvailableSlot = ({
  context,
  maxDays,
}: {
  context: SchedulingContext;
  maxDays: number;
}) => {
  const baseNow = context.now.setZone(context.timezone);

  return findFirstSlotAcrossDays(
    context,
    Array.from({ length: maxDays }, (_, i) => baseNow.plus({ days: i }))
  );
};

const findSchedulableDateFallbackSlot = ({
  context,
  maxDays,
  t,
}: {
  context: SchedulingContext;
  maxDays: number;
  t: any;
}) => {
  const { timezone } = context;

  const schedulableDates: ScheduleDateType[] = getSchedulableDateOptions({
    context,
    scheduleDateRange: maxDays - 1,
    t,
  });

  return findFirstSlotAcrossDays(
    context,
    schedulableDates.map((schedulableDate) =>
      DateTime.fromJSDate(new Date(schedulableDate.dateObject), {
        zone: timezone,
      })
    )
  );
};

/**
 * - `{ type, epoch }` — an ASAP quote.
 * - `string`         — a localized "Today, 9:15 AM" style display for a scheduled slot.
 * - `""`             — delivery selected but no delivery estimate is available yet.
 * - `null`           — nothing schedulable within `maxDays`.
 */
type PickupTimeResult = { type: PickupTimeType; epoch: number } | string | null;

interface CalculatePickupTimeOptions {
  currentTime: DateTime;
  merchant: Merchant;
  businessHours: any;
  orderType: OrderType;
  daypartMenu?: DayPart | null;
  t?: any;
}

export const calculatePickupTime = ({
  currentTime,
  merchant,
  businessHours,
  orderType,
  daypartMenu,
  t,
}: CalculatePickupTimeOptions): PickupTimeResult => {
  const { leadTime, timezone } = merchant;
  const { maxDays = 3 } = merchant.scheduledOrdering ?? {};
  const busyModeFields = toBusyModeFields(merchant);
  /** Single canonical "now" for this run. Every helper receives exactly this value. */
  const now = currentTime.setZone(timezone);
  const { deliveryTime, pickupTime } = getFromSessionStorage<OrderEstimates>(
    SessionStorageKey.ORDER_ESTIMATES,
    {} as OrderEstimates
  );
  const deliveryDelay = getDeliveryDelayMinutes({
    orderType,
    deliveryTime,
    pickupTime,
  });

  // Presence of the estimate, not the size of the delay: a genuine quote whose pickup and
  // delivery timestamps land on the same minute yields a 0 delay and must still be honored.
  // This happens when order type is changed to delivery but delivery address is not entered
  const hasDeliveryEstimate = Boolean(deliveryTime && pickupTime);

  if (orderType === OrderType.DELIVERY && !hasDeliveryEstimate) {
    return "";
  }

  const context: SchedulingContext = {
    now,
    timezone,
    locale: "en-US",
    hours: businessHours,
    leadTime,
    busyModeFields,
    deliveryDelay,
    orderType,
    daypartMenu,
  };

  // ASAP quotes are unscheduled, so no fulfillment timestamp is passed: busy mode falls back
  // to defaultLeadTime the moment busyModeExpireTime has passed, even if the cached merchant
  // record still carries the elevated leadTime.
  const asapLeadTime = getEffectiveLeadTime(
    leadTime,
    busyModeFields,
    now.toMillis()
  );
  const targetPickupTime = now.plus({
    minutes: asapLeadTime + deliveryDelay,
  });
  const today = now.startOf("day");
  const tomorrow = today.plus({ days: 1 });
  const sessionTime = getFromSessionStorage<ScheduledTime>(
    SessionStorageKey.SCHEDULED_TIME,
    {} as ScheduledTime
  );
  const sessionOrderType = sessionTime?.orderType || OrderType.PICKUP;

  /**
   * If orderType stored in session storage is not same as orderType then ignore the value stored in session stirage and calculate again
   */
  const storedSessionPickupDisplay = getStoredSessionPickupDisplay(
    sessionTime,
    orderType,
    timezone,
    now,
    today,
    tomorrow,
    t
  );

  if (storedSessionPickupDisplay) {
    return storedSessionPickupDisplay;
  }

  const canShowASAP = isWithinBusinessHours(
    now,
    businessHours,
    timezone,
    daypartMenu
  );

  if (canShowASAP) {
    saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
      orderType: sessionOrderType,
      asap: true,
      date: {
        displayValue: t("today"),
        dateObject: now.toJSDate(),
      },
      time: {
        displayValue: "",
        timeObject: now.toJSDate(),
      },
    });
    return {
      type: PickupTimeType.ASAP,
      epoch: targetPickupTime.toMillis(),
    };
  }

  const nextAvailableSlot = findNextAvailableSlot({ context, maxDays });

  if (nextAvailableSlot) {
    saveScheduledSession(sessionOrderType, nextAvailableSlot);
    return getFormattedPickupDisplay(nextAvailableSlot, today, tomorrow, t);
  }

  const schedulableFallbackSlot = findSchedulableDateFallbackSlot({
    context,
    maxDays,
    t,
  });

  if (!schedulableFallbackSlot) {
    return null;
  }

  saveScheduledSession(sessionOrderType, schedulableFallbackSlot);
  return getFormattedPickupDisplay(schedulableFallbackSlot, today, tomorrow, t);
};

export const getCartStatus = (
  storedCart: CartItem[],
  storedGiftCard: GiftCard | null,
  tableData?: TableData | null
) => {
  const cartValue =
    storedCart.reduce((adder: number, item: any) => {
      if (item?.value?.serviceDetails?.priceType === "PER_UNIT") {
        return adder + 1;
      }
      return adder + (item?.value?.quantity || 0);
    }, 0) + (storedGiftCard ? 1 : 0);

  const scanToOrderCartLength = tableData?.order?.cart?.items?.length
    ? tableData?.order?.cart?.items?.length
    : 0;

  const shouldShow =
    storedCart.length > 0 || !!storedGiftCard || scanToOrderCartLength > 0;

  return {
    cartValue,
    shouldShow,
  };
};
